import { useState, useEffect, useRef } from 'react';
import { Plus, Edit, Trash2, Check, XCircle, Upload, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import axios from 'axios';
import { API, getAuthHeader } from './api';

export function StartupsManagement() {
  const [startups, setStartups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingStartup, setEditingStartup] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '', tagline: '', description: '', logo_url: '', sector: '', stage: '',
    founders: [], problem: '', solution: '', milestones: '', website_url: '',
    contact_email: '', is_featured: false
  });

  useEffect(() => { fetchStartups(); }, []);

  const fetchStartups = async () => {
    try { const response = await axios.get(`${API}/startups`); setStartups(response.data); }
    catch { /* silent */ }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...formData, founders: typeof formData.founders === 'string' ? formData.founders.split(',').map(f => f.trim()) : formData.founders };
      if (editingStartup) {
        await axios.put(`${API}/startups/${editingStartup.id}`, payload, { headers: getAuthHeader() });
        toast.success('Startup updated');
      } else {
        await axios.post(`${API}/startups`, payload, { headers: getAuthHeader() });
        toast.success('Startup created');
      }
      setIsModalOpen(false); setEditingStartup(null); fetchStartups();
    } catch (error) { toast.error('Failed to save startup'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this startup?')) return;
    try { await axios.delete(`${API}/startups/${id}`, { headers: getAuthHeader() }); toast.success('Startup deleted'); fetchStartups(); }
    catch (error) { toast.error('Failed to delete'); }
  };

  const openModal = (startup = null) => {
    if (startup) { setEditingStartup(startup); setFormData({ ...startup, founders: startup.founders?.join(', ') || '' }); }
    else { setEditingStartup(null); setFormData({ name: '', tagline: '', description: '', logo_url: '', sector: '', stage: '', founders: '', problem: '', solution: '', milestones: '', website_url: '', contact_email: '', is_featured: false }); }
    setIsModalOpen(true);
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!['image/png', 'image/jpeg', 'image/jpg'].includes(file.type)) {
      toast.error('Only PNG and JPG images are allowed');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be under 5MB');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/upload/logo`, fd);
      setFormData(prev => ({ ...prev, logo_url: res.data.path }));
      toast.success('Logo uploaded');
    } catch (err) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-[#231F20] font-['Bricolage_Grotesque']">Manage Startups</h1>
        <Button onClick={() => openModal()} className="bg-[#075FA3] hover:bg-[#0077B6]" data-testid="add-startup-btn"><Plus className="w-4 h-4 mr-2" /> Add Startup</Button>
      </div>
      <Card>
        <Table>
          <TableHeader>
            <TableRow><TableHead>Name</TableHead><TableHead>Sector</TableHead><TableHead>Stage</TableHead><TableHead>Featured</TableHead><TableHead>Actions</TableHead></TableRow>
          </TableHeader>
          <TableBody>
            {startups.map((startup) => (
              <TableRow key={startup.id}>
                <TableCell className="font-medium">{startup.name}</TableCell>
                <TableCell>{startup.sector}</TableCell>
                <TableCell><Badge variant="outline">{startup.stage}</Badge></TableCell>
                <TableCell>{startup.is_featured ? <Check className="w-5 h-5 text-green-500" /> : <XCircle className="w-5 h-5 text-gray-300" />}</TableCell>
                <TableCell>
                  <div className="flex gap-2">
                    <Button size="sm" variant="ghost" onClick={() => openModal(startup)}><Edit className="w-4 h-4" /></Button>
                    <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDelete(startup.id)}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {startups.length === 0 && !loading && (
              <TableRow><TableCell colSpan={5} className="text-center text-[#58595B] py-8">No startups yet</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Card>
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingStartup ? 'Edit Startup' : 'Add Startup'}</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Name *</Label><Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required /></div>
              <div><Label>Sector *</Label>
                <Select value={formData.sector} onValueChange={(v) => setFormData({ ...formData, sector: v })}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{['Fintech', 'EdTech', 'HealthTech', 'Sustainability', 'AgriTech', 'SaaS', 'E-Commerce', 'Social Impact', 'Other'].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Tagline *</Label><Input value={formData.tagline} onChange={(e) => setFormData({ ...formData, tagline: e.target.value })} required /></div>
            <div><Label>Description *</Label><Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} required /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Stage *</Label>
                <Select value={formData.stage} onValueChange={(v) => setFormData({ ...formData, stage: v })}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{['Idea', 'Validation', 'Prototype', 'MVP', 'Early Revenue', 'Scaling'].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Logo URL</Label><Input value={formData.logo_url} onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })} placeholder="Paste URL or upload below" /></div>
            </div>
            <div>
              <input type="file" accept="image/png,image/jpeg,image/jpg" ref={fileInputRef} onChange={handleLogoUpload} className="hidden" data-testid="logo-file-input" />
              <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()} disabled={uploading} className="w-full" data-testid="upload-logo-btn">
                {uploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading...</> : <><Upload className="w-4 h-4 mr-2" /> Upload Logo Image</>}
              </Button>
              {formData.logo_url && (
                <div className="mt-2 flex items-center gap-3">
                  <img src={formData.logo_url.startsWith('cfe-krea/') ? `${API}/files/${formData.logo_url}` : formData.logo_url} alt="Logo preview" className="w-12 h-12 rounded-lg object-cover border" />
                  <span className="text-xs text-[#58595B] truncate flex-1">{formData.logo_url}</span>
                </div>
              )}
            </div>
            <div><Label>Founders (comma-separated)</Label><Input value={formData.founders} onChange={(e) => setFormData({ ...formData, founders: e.target.value })} /></div>
            <div><Label>Problem</Label><Textarea value={formData.problem} onChange={(e) => setFormData({ ...formData, problem: e.target.value })} /></div>
            <div><Label>Solution</Label><Textarea value={formData.solution} onChange={(e) => setFormData({ ...formData, solution: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Website URL</Label><Input value={formData.website_url} onChange={(e) => setFormData({ ...formData, website_url: e.target.value })} /></div>
              <div><Label>Contact Email</Label><Input value={formData.contact_email} onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })} /></div>
            </div>
            <Button type="submit" className="w-full bg-[#075FA3]">{editingStartup ? 'Update' : 'Create'} Startup</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
